import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Button,
  Badge,
  Snackbar,
  Alert,
  GlobalStyles,
} from '@mui/material';
import { collection, query, where, onSnapshot, Timestamp } from 'firebase/firestore';
import { db } from '../services/firebase';
import { notificationService } from '../services/notificationService';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Nightlife as NightlifeIcon,
  ReceiptLong as ReceiptLongIcon,
  Liquor as LiquorIcon,
  Payments as PaymentsIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  LocalOffer as LocalOfferIcon,
  RoomService as RoomServiceIcon,
  FactCheck as FactCheckIcon,
  Store as StoreIcon,
  People as PeopleIcon,
  MenuBook as MenuBookIcon,
  Settings as SettingsIcon,
  EventNote as EventNoteIcon,
  Logout as LogoutIcon,
  PersonSearch as PersonSearchIcon,
  Restaurant as RestaurantIcon,
  Category as CategoryIcon,
  Print as PrintIcon,
  TableBar as TableBarIcon,
  Receipt as ReceiptIcon,
  Badge as BadgeIcon,
  CalendarMonth as CalendarIcon,
  BarChart as BarChartIcon,
  Notifications as NotificationsIcon,
  Language as LanguageIcon,
  Block as BlockIcon,
  PointOfSale as PointOfSaleIcon,
  VolumeUp as VolumeUpIcon,
  VolumeOff as VolumeOffIcon,
  NotificationsActive as NotificationsActiveIcon,
  AttachMoney as AttachMoneyIcon,
  Celebration as CelebrationIcon,
  LocalOffer as CouponIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { resolveCategory, isMenuVisible } from '../utils/menuScope';
import { useLanguage } from '../contexts/LanguageContext';
import { usingEmulator } from '../services/firebase';

/** エミュレータ帯の高さ。body の余白と揃える */
const EMULATOR_BANNER_HEIGHT = 24;

const drawerWidth = 240;

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { shop, currentRole, availableShops, switchShop, signOut } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  // 通知関連のstate
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [pendingOrderCount, setPendingOrderCount] = useState(0);
  const [pendingCallCount, setPendingCallCount] = useState(0);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'info' | 'warning' }>({
    open: false,
    message: '',
    severity: 'info',
  });

  // 前回の注文数・呼び出し数を追跡
  const prevOrderCountRef = useRef(0);
  const prevCallCountRef = useRef(0);
  const isInitialLoadRef = useRef(true);

  // 通知サービスの初期化
  useEffect(() => {
    notificationService.initialize();
    // ブラウザ通知の許可をリクエスト
    notificationService.requestPermission();
  }, []);

  // 音声通知の有効/無効を切り替え
  const toggleSound = useCallback(() => {
    const newState = !soundEnabled;
    setSoundEnabled(newState);
    notificationService.setSoundEnabled(newState);
    notificationService.setVibrationEnabled(newState);

    // AudioContextを再開（ユーザー操作があったため）
    notificationService.resume();

    // テスト音を再生（有効にした場合）
    if (newState) {
      notificationService.testSound('order');
    }
  }, [soundEnabled]);

  // 新規注文の監視
  useEffect(() => {
    if (!shop?.id) return;

    // 過去30分以内のpending注文を監視
    const thirtyMinutesAgo = new Date(Date.now() - 30 * 60 * 1000);

    const ordersQuery = query(
      collection(db, 'orders'),
      where('shopId', '==', shop.id),
      where('status', '==', 'pending'),
      where('createdAt', '>=', Timestamp.fromDate(thirtyMinutesAgo))
    );

    const unsubscribe = onSnapshot(ordersQuery, (snapshot) => {
      const count = snapshot.docs.length;
      setPendingOrderCount(count);

      // 初回ロード後、新規注文があれば通知
      if (!isInitialLoadRef.current && count > prevOrderCountRef.current) {
        console.log('Layout: New order detected!', count - prevOrderCountRef.current, 'new orders');
        if (soundEnabled) {
          notificationService.notifyOrder();
        }
        setSnackbar({
          open: true,
          message: `新しい注文が ${count - prevOrderCountRef.current} 件入りました`,
          severity: 'info',
        });
      }

      prevOrderCountRef.current = count;
    });

    return () => unsubscribe();
  }, [shop?.id, soundEnabled]);

  // 呼び出しリクエストの監視
  useEffect(() => {
    if (!shop?.id) return;

    // 過去10分以内のpending呼び出しを監視
    const tenMinutesAgo = new Date(Date.now() - 10 * 60 * 1000);

    const callsQuery = query(
      collection(db, 'callRequests'),
      where('shopId', '==', shop.id),
      where('status', '==', 'pending'),
      where('requestedAt', '>=', Timestamp.fromDate(tenMinutesAgo))
    );

    const unsubscribe = onSnapshot(callsQuery, (snapshot) => {
      const count = snapshot.docs.length;
      setPendingCallCount(count);

      // 初回ロード後、新規呼び出しがあれば通知
      if (!isInitialLoadRef.current && count > prevCallCountRef.current) {
        console.log('Layout: New call request detected!', count - prevCallCountRef.current, 'new calls');
        if (soundEnabled) {
          notificationService.notifyCall();
        }
        // 呼び出しがあったテーブル情報を取得
        const newCalls = snapshot.docs.slice(-1 * (count - prevCallCountRef.current));
        const tableNumbers = newCalls.map(doc => doc.data().tableNumber).join(', ');
        setSnackbar({
          open: true,
          message: `テーブル ${tableNumbers} から呼び出しがあります`,
          severity: 'warning',
        });
      }

      prevCallCountRef.current = count;
    });

    return () => unsubscribe();
  }, [shop?.id, soundEnabled]);

  // 初回ロード完了フラグ
  useEffect(() => {
    const timer = setTimeout(() => {
      isInitialLoadRef.current = false;
    }, 3000);
    return () => clearTimeout(timer);
  }, [shop?.id]);

  const cycleLanguage = () => {
    // ja -> en -> th -> ja の順で切り替え
    console.log('cycleLanguage called, current language:', language);
    if (language === 'ja') {
      setLanguage('en');
      console.log('Changing to: en');
    } else if (language === 'en') {
      setLanguage('th');
      console.log('Changing to: th');
    } else {
      setLanguage('ja');
      console.log('Changing to: ja');
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // 権限マトリクスを定義
  type MenuItemRole = 'owner' | 'manager' | 'staff';

  const baseMenuItems: Array<{
    textKey: string;
    icon: React.ReactElement;
    path: string;
    roles: MenuItemRole[];
  }> = [
    // 夜職の1日の流れに沿って並べる。
    // 卓を開ける → 会計する → バックを承認する → 締めて給与を出す。
    // 予約と多業種向けの画面は外した（美容室などは別アプリで扱う）。
    { textKey: 'dashboard', icon: <DashboardIcon />, path: '/dashboard', roles: ['owner', 'manager', 'staff'] },
    // 複数店を持つ人にだけ出す。1店だけの人には要らない項目になる。
    // 出す・出さないは下の filteredBaseMenuItems で決める。
    { textKey: 'allShops', icon: <StoreIcon />, path: '/all-shops', roles: ['owner', 'manager'] },

    // --- 当日の営業 ---
    { textKey: 'floorMap', icon: <TableBarIcon />, path: '/floor-map', roles: ['owner', 'manager', 'staff'] },
    { textKey: 'customers', icon: <PersonSearchIcon />, path: '/customers', roles: ['owner', 'manager'] },
    { textKey: 'bottleKeeps', icon: <LiquorIcon />, path: '/bottle-keeps', roles: ['owner', 'manager'] },
    { textKey: 'receivables', icon: <ReceiptLongIcon />, path: '/receivables', roles: ['owner', 'manager'] },

    // --- 人 ---
    { textKey: 'employees', icon: <BadgeIcon />, path: '/employees', roles: ['owner', 'manager'] },
    { textKey: 'shifts', icon: <CalendarIcon />, path: '/shifts', roles: ['owner', 'manager'] },
    { textKey: 'attendances', icon: <EventNoteIcon />, path: '/attendances', roles: ['owner', 'manager'] },

    // --- お金 ---
    { textKey: 'allowanceTypes', icon: <LocalOfferIcon />, path: '/allowance-types', roles: ['owner', 'manager'] },
    { textKey: 'backApproval', icon: <AttachMoneyIcon />, path: '/back-approval', roles: ['owner', 'manager'] },
    { textKey: 'payouts', icon: <PaymentsIcon />, path: '/payouts', roles: ['owner', 'manager'] },
    { textKey: 'payroll', icon: <AccountBalanceWalletIcon />, path: '/payroll', roles: ['owner', 'manager'] },
    // 売上の実績を先に置く。店主がいちばん見たいのはここ。
    // 「売上分析」はスタッフ別・言語別で、その次に見るもの。
    { textKey: 'salesReport', icon: <BarChartIcon />, path: '/sales-report', roles: ['owner', 'manager'] },
    { textKey: 'analytics', icon: <BarChartIcon />, path: '/analytics', roles: ['owner', 'manager'] },

    // --- 店の運営 ---
    { textKey: 'eventManagement', icon: <CelebrationIcon />, path: '/events', roles: ['owner', 'manager'] },
    { textKey: 'couponManagement', icon: <CouponIcon />, path: '/coupons', roles: ['owner', 'manager'] },
    { textKey: 'shopSettings', icon: <StoreIcon />, path: '/settings/shop', roles: ['owner'] },
    { textKey: 'lineSettings', icon: <SettingsIcon />, path: '/line-settings', roles: ['owner'] },
    { textKey: 'planSettings', icon: <SettingsIcon />, path: '/settings/plan', roles: ['owner'] },
    { textKey: 'timeChargeSettings', icon: <SettingsIcon />, path: '/settings/time-charge', roles: ['owner'] },
    // Stripe Connect（お客様が店に払う分の受け取り口）はメニューから外した。
    //
    //   本番の 13 店のうち、繋いでいる店は 0 件だった。
    //   使っていない設定が並んでいると、店は「これも設定しないと
    //   使えないのか」と迷う。必要になった店にだけ案内すればよい。
    //   画面と関数は残してあるので、/settings/stripe-connect を
    //   直接開けば今までどおり設定できる。
    { textKey: 'setupCheck', icon: <FactCheckIcon />, path: '/setup-check', roles: ['owner', 'manager'] },
  ];

  // 店の業種。メニューの出し分けに使う。
  //
  //   もとは `shop?.businessTypeCode === '41'` で飲食店を判定していたが、
  //   実データに businessTypeCode は入っていない（本番13店すべて空）。
  //   常に false になるので、判定そのものが効いていなかった。
  //   業種の出どころは utils/menuScope.ts の1箇所にまとめた。
  const shopCategory = React.useMemo(() => resolveCategory(shop), [shop]);
  const isRestaurant = shopCategory === 'restaurant';

  // 役割と業種の両方でメニューを絞る。
  //
  //   出し分けが1つも無く、全業種に31項目すべてが並んでいた。
  //   居酒屋のオーナーには、そのうち8項目が一生使わないものだった。
  //   使わない項目が並ぶと「これも設定しないと使えないのか」と手が止まる。
  const filteredBaseMenuItems = React.useMemo(() => {
    const byRole = currentRole
      ? baseMenuItems.filter(item => item.roles.includes(currentRole))
      : baseMenuItems;
    const byCategory = byRole.filter(item => isMenuVisible(item.textKey, shopCategory));
    // 店を1つしか持たない人に「店舗の一覧」は要らない。
    // 出しても中身が1行だけで、かえって迷わせる。
    return (availableShops?.length ?? 0) > 1
      ? byCategory
      : byCategory.filter(item => item.textKey !== 'allShops');
  }, [currentRole, shopCategory, availableShops]);

  // POSレジ・会計メニュー（全業種共通 + 注文・テーブル管理は飲食店のみ）
  const posMenuItems: Array<{
    textKey: string;
    icon: React.ReactElement;
    path: string;
    roles: MenuItemRole[];
    requiresMobileOrder?: boolean;
  }> = [
    { textKey: 'proxyOrder', icon: <RoomServiceIcon />, path: '/proxy-order', roles: ['owner', 'manager', 'staff'] },
    { textKey: 'checkoutRegister', icon: <PointOfSaleIcon />, path: '/mobile-order/checkout', roles: ['owner', 'manager', 'staff'] },
    { textKey: 'productCategories', icon: <CategoryIcon />, path: '/mobile-order/categories', roles: ['owner', 'manager'] },
    { textKey: 'productManagement', icon: <RestaurantIcon />, path: '/mobile-order/products', roles: ['owner', 'manager'] },
    { textKey: 'optionManagement', icon: <SettingsIcon />, path: '/mobile-order/options', roles: ['owner', 'manager'] },
    { textKey: 'orderManagement', icon: <ReceiptIcon />, path: '/mobile-order/orders', roles: ['owner', 'manager', 'staff'], requiresMobileOrder: true },
    { textKey: 'tableManagement', icon: <TableBarIcon />, path: '/mobile-order/tables', roles: ['owner', 'manager', 'staff'], requiresMobileOrder: true },
  ];

  // モバイルオーダーメニュー（飲食店専用）
  const mobileOrderMenuItems: Array<{
    textKey: string;
    icon: React.ReactElement;
    path: string;
    roles: MenuItemRole[];
    alwaysShow?: boolean; // mobileOrderEnabled=false でも表示
  }> = [
    { textKey: 'blacklist', icon: <BlockIcon />, path: '/blacklist', roles: ['owner', 'manager'] },
    { textKey: 'planManagement', icon: <MenuBookIcon />, path: '/mobile-order/plans', roles: ['owner', 'manager'] },
    { textKey: 'printerSettings', icon: <PrintIcon />, path: '/mobile-order/printers', roles: ['owner', 'manager'] },
    { textKey: 'mobileOrderSettings', icon: <SettingsIcon />, path: '/settings/mobile-order', roles: ['owner'], alwaysShow: true },
  ];

  // POSメニューもロールでフィルタ（注文管理・テーブル管理はモバイルオーダー有効時のみ）
  const filteredPosMenuItems = React.useMemo(() => {
    let items = posMenuItems;
    if (currentRole) {
      items = items.filter(item => item.roles.includes(currentRole));
    }
    // requiresMobileOrderがtrueのアイテムはモバイルオーダー有効時のみ表示
    items = items.filter(item => {
      if (item.requiresMobileOrder) {
        return shop?.mobileOrderEnabled && isRestaurant;
      }
      return true;
    });
    // 業種でも絞る（基本メニューと同じ入口を使う）
    items = items.filter(item => isMenuVisible(item.textKey, shopCategory));
    return items;
  }, [currentRole, shop?.mobileOrderEnabled, isRestaurant, shopCategory]);

  // モバイルオーダーメニューもロールでフィルタ
  // alwaysShow=true のアイテムはmobileOrderEnabled=falseでも表示
  const filteredMobileOrderMenuItems = React.useMemo(() => {
    let items = mobileOrderMenuItems;
    if (currentRole) {
      items = items.filter(item => item.roles.includes(currentRole));
    }
    // mobileOrderEnabledがfalseの場合、alwaysShow=trueのアイテムのみ表示
    if (!shop?.mobileOrderEnabled) {
      items = items.filter(item => item.alwaysShow);
    }
    // 業種でも絞る（基本メニューと同じ入口を使う）
    items = items.filter(item => isMenuVisible(item.textKey, shopCategory));
    return items;
  }, [currentRole, shop?.mobileOrderEnabled, shopCategory]);

  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          {t('shopManagement')}
        </Typography>
      </Toolbar>
      <Divider />
      {shop && (
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {t('shopName')}
          </Typography>
          <Typography variant="body1" fontWeight="bold">
            {shop.shopName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('shopCode')}: {shop.shopCode}
          </Typography>
        </Box>
      )}
      <Divider />

      {/* POSレジ・会計（全業種共通） - 最上部に配置 */}
      <Box sx={{ px: 2, py: 1, bgcolor: 'success.50' }}>
        <Typography variant="caption" color="success.main" fontWeight="bold">
          {t('posSection')}
        </Typography>
      </Box>
      <List>
        {filteredPosMenuItems.map((item) => (
          <ListItem key={item.textKey} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => {
                navigate(item.path);
                setMobileOpen(false);
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={t(item.textKey)} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Divider />
      <List>
        {filteredBaseMenuItems.map((item) => (
          <ListItem key={item.textKey} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => {
                navigate(item.path);
                setMobileOpen(false);
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={t(item.textKey)} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      {/* モバイルオーダー設定（飲食店専用） - mobileOrderEnabled=falseでも設定メニューは表示 */}
      {isRestaurant && filteredMobileOrderMenuItems.length > 0 && (
        <>
          <Divider />
          <Box sx={{ px: 2, py: 1, bgcolor: 'primary.50' }}>
            <Typography variant="caption" color="primary.main" fontWeight="bold">
              {t('mobileOrderSection')}
            </Typography>
          </Box>
          <List>
            {filteredMobileOrderMenuItems.map((item) => (
              <ListItem key={item.textKey} disablePadding>
                <ListItemButton
                  selected={location.pathname === item.path}
                  onClick={() => {
                    navigate(item.path);
                    setMobileOpen(false);
                  }}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={t(item.textKey)} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </>
      )}
      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary={t('logout')} />
          </ListItemButton>
        </ListItem>
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {shop?.shopName || t('shopManagement')}
          </Typography>

          {/* 通知バッジ */}
          {(pendingOrderCount > 0 || pendingCallCount > 0) && (
            <Badge
              badgeContent={pendingOrderCount + pendingCallCount}
              color="error"
              sx={{ mr: 2 }}
            >
              <NotificationsActiveIcon sx={{ color: 'warning.light', animation: 'pulse 1s infinite' }} />
            </Badge>
          )}

          {/* 音声通知切り替えボタン */}
          <IconButton
            color="inherit"
            onClick={toggleSound}
            sx={{ mr: 1 }}
            title={soundEnabled ? '通知音ON' : '通知音OFF'}
          >
            {soundEnabled ? <VolumeUpIcon /> : <VolumeOffIcon />}
          </IconButton>

          <IconButton color="inherit" onClick={cycleLanguage} sx={{ mr: 1 }}>
            <LanguageIcon />
          </IconButton>
          <Button color="inherit" onClick={handleLogout} sx={{ display: { xs: 'none', sm: 'block' } }}>
            {t('logout')}
          </Button>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar />
        {children}
      </Box>

      {/* エミュレータ接続中の表示。
          固定配置のままだとヘッダーに重なるので、body 側に余白を作って押し下げる。 */}
      {usingEmulator && (
        <>
          <GlobalStyles styles={{ body: { paddingTop: `${EMULATOR_BANNER_HEIGHT}px` } }} />
          <Box sx={{
            position: 'fixed', top: 0, left: 0, right: 0, zIndex: (t) => t.zIndex.drawer + 2,
            height: EMULATOR_BANNER_HEIGHT, lineHeight: `${EMULATOR_BANNER_HEIGHT}px`,
            bgcolor: 'warning.main', color: 'warning.contrastText',
            textAlign: 'center', fontSize: 12, fontWeight: 'bold',
          }}>
            エミュレータ接続中 — 本番データには書き込みません
          </Box>
        </>
      )}

      {/* 通知スナックバー */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Layout;
